import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { KeyRound, Plus, Search, Trash2, Archive, ArchiveRestore, FileText, FileDown } from "lucide-react";

export const STATUT_REMISE = { en_cours: ["En cours", "bg-dd-blue/10 text-dd-blue"], en_retard: ["En retard", "bg-destructive/10 text-destructive"], restituee: ["Restituée", "bg-emerald-100 text-emerald-800"] };
const fmt = (d) => (d ? new Date(d).toLocaleDateString("fr-CH") : "—");
const FILTRES = [["toutes", "Toutes"], ["en_cours", "En cours"], ["terminees", "Terminées"], ["archivees", "Archivées"]];

export default function RemisesList() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [filtre, setFiltre] = useState("toutes");
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/remises").then((r) => setItems(r.data)).finally(() => setLoading(false)); }, []);

  const creer = async () => {
    try { const { data } = await api.post("/remises", {}); navigate(`/cles/${data.id}`); } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const archiver = async (e, it, archive) => {
    e.stopPropagation();
    try { const { data } = await api.post(`/remises/${it.id}/archive`, { archive }); setItems((xs) => xs.map((x) => (x.id === it.id ? { ...x, archive: data.archive } : x))); toast.success(archive ? "Remise archivée" : "Remise désarchivée"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const supprimer = async (e, it) => {
    e.stopPropagation();
    if (!window.confirm(`Supprimer définitivement cette remise${it.entreprise?.nom ? ` (${it.entreprise.nom})` : ""} ? Cette action est irréversible.`)) return;
    try { await api.delete(`/remises/${it.id}`); setItems((xs) => xs.filter((x) => x.id !== it.id)); toast.success("Remise supprimée"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const exporter = (format) => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/remises/export?format=${format}&filtre=${filtre}`, "_blank");

  const nb = {
    toutes: items.filter((it) => !it.archive).length,
    en_cours: items.filter((it) => !it.archive && (it.statut === "en_cours" || it.statut === "en_retard")).length,
    terminees: items.filter((it) => !it.archive && it.statut === "restituee").length,
    archivees: items.filter((it) => it.archive).length,
  };
  const list = items
    .filter((it) => !q || JSON.stringify([it.immeuble, it.adresse, it.entreprise?.nom, it.employe]).toLowerCase().includes(q.toLowerCase()))
    .filter((it) => {
      if (filtre === "archivees") return it.archive;
      if (it.archive) return false;
      if (filtre === "en_cours") return it.statut === "en_cours" || it.statut === "en_retard";
      if (filtre === "terminees") return it.statut === "restituee";
      return true;
    });

  return (
    <div data-testid="remises-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div><h1 className="text-3xl font-bold text-dd-blue">Remise de clés</h1><p className="text-sm text-dd-warm mt-1">Clés confiées à des entreprises : remise signée, suivi du retour, restitution signée.</p></div>
        <Button onClick={creer} className="h-11 bg-dd-blue text-white hover:bg-dd-night" data-testid="remise-new-btn"><Plus size={18} className="mr-2" /> Nouvelle remise</Button>
      </div>
      <div className="relative mb-4"><Search size={16} className="absolute left-3 top-3.5 text-dd-warm" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher une entreprise, un immeuble…" className="h-11 pl-9 bg-white" data-testid="remise-search" /></div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex flex-wrap gap-2" data-testid="remise-filtres">
          {FILTRES.map(([k, label]) => (
            <button key={k} onClick={() => setFiltre(k)} data-testid={`remise-filtre-${k}`}
              className={`h-9 px-3.5 rounded-full text-sm font-medium border transition-colors ${filtre === k ? "bg-dd-blue text-white border-dd-blue" : "bg-white text-dd-night border-dd-light hover:bg-dd-light/60"}`}>
              {label} <span className={filtre === k ? "text-white/70" : "text-dd-warm"}>{nb[k]}</span>
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => exporter("csv")} className="h-9 bg-white border-dd-light text-dd-night hover:bg-dd-light/60" data-testid="remise-export-csv"><FileText size={15} className="mr-1.5" /> CSV</Button>
          <Button variant="outline" onClick={() => exporter("pdf")} className="h-9 bg-white border-dd-light text-dd-night hover:bg-dd-light/60" data-testid="remise-export-pdf"><FileDown size={15} className="mr-1.5" /> PDF</Button>
        </div>
      </div>
      <div className="bg-white rounded-lg border border-dd-light overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="bg-dd-blue text-white text-left"><th className="px-4 py-3 font-semibold">Immeuble / objet</th><th className="px-4 py-3 font-semibold">Entreprise</th><th className="px-4 py-3 font-semibold">Clés</th><th className="px-4 py-3 font-semibold">Remise</th><th className="px-4 py-3 font-semibold">Retour prévu</th><th className="px-4 py-3 font-semibold">Statut</th><th className="px-4 py-3 font-semibold text-right">Actions</th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={7} className="px-4 py-10 text-center text-dd-warm">Chargement…</td></tr>
              : list.length === 0 ? <tr><td colSpan={7} className="px-4 py-16 text-center text-dd-warm"><KeyRound className="mx-auto mb-3 text-dd-light" size={40} />Aucune remise {filtre === "archivees" ? "archivée" : "de clés"}.</td></tr>
              : list.map((it) => { const [l, c] = STATUT_REMISE[it.statut]; return (
                <tr key={it.id} onClick={() => navigate(`/cles/${it.id}`)} className="border-t border-dd-light hover:bg-dd-light/40 cursor-pointer" data-testid={`remise-row-${it.id}`}>
                  <td className="px-4 py-3 text-dd-night font-medium">{it.immeuble || it.adresse || "—"}{it.numero_objet ? <span className="text-dd-warm"> · {it.numero_objet}</span> : ""}</td>
                  <td className="px-4 py-3">{it.entreprise?.nom || "—"}<div className="text-xs text-dd-warm">{it.employe?.prenom} {it.employe?.nom}</div></td>
                  <td className="px-4 py-3">{(it.cles || []).reduce((s, c) => s + (Number(c.nombre) || 1), 0)}</td>
                  <td className="px-4 py-3">{fmt(it.remise?.signe_le)}</td>
                  <td className="px-4 py-3">{fmt(it.retour_prevu)}</td>
                  <td className="px-4 py-3"><Badge className={`border-0 ${c}`}>{l}</Badge></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                      {it.archive ? (
                        <button onClick={(e) => archiver(e, it, false)} title="Désarchiver" data-testid={`remise-unarchive-${it.id}`} className="h-9 w-9 flex items-center justify-center rounded-md border border-dd-light text-dd-blue hover:bg-dd-light/60"><ArchiveRestore size={16} /></button>
                      ) : it.statut === "restituee" ? (
                        <button onClick={(e) => archiver(e, it, true)} title="Archiver" data-testid={`remise-archive-${it.id}`} className="h-9 w-9 flex items-center justify-center rounded-md border border-dd-light text-dd-warm hover:bg-dd-light/60 hover:text-dd-blue"><Archive size={16} /></button>
                      ) : null}
                      <button onClick={(e) => supprimer(e, it)} title="Supprimer" data-testid={`remise-delete-${it.id}`} className="h-9 w-9 flex items-center justify-center rounded-md border border-dd-light text-destructive hover:bg-destructive/10"><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>); })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
